import { useCallback, useEffect, useState } from "react";
import Title from "../../components/common/title/Title";
import { getApi, putApi } from "../../services/api/globalApi";
import Pagination from "../../components/common/pagination/Pagination";
import { useNavigate } from "react-router";
import { fileUrl } from "../../services/config";
import ProductFilter from "./ProductFilter";
import { digitsThree } from "../../utils/price";
import DefaultImage from "../../components/common/default-image/DefaultImage";
import * as XLSX from "xlsx";
import Button from "../../components/common/button/Button";
import PriceImportModal from "./PriceImportModal";
import {
  errorMessage,
  successMessage,
} from "../../utils/toastify-message/ToastifyMessage";

function BulkActionBar({ selectedProductIds, selectedVariations, bulkLoading, onAvailable, onUnavailable, onClear }) {
  const totalProducts = selectedProductIds.size;
  const totalVariations = [...selectedVariations.values()].reduce((sum, s) => sum + s.size, 0);
  const total = totalProducts + totalVariations;
  if (total === 0) return null;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 my-4 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl">
      <span className="text-sm font-semibold text-blue-700 dark:text-blue-300 shrink-0">
        {totalProducts > 0 && `${totalProducts} محصول`}
        {totalProducts > 0 && totalVariations > 0 && " و "}
        {totalVariations > 0 && `${totalVariations} آپشن`}
        {" انتخاب شده"}
      </span>
      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        <button
          onClick={onAvailable}
          disabled={bulkLoading}
          type="button"
          className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {bulkLoading ? "در حال اعمال..." : "موجود کردن"}
        </button>
        <button
          onClick={onUnavailable}
          disabled={bulkLoading}
          type="button"
          className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {bulkLoading ? "در حال اعمال..." : "ناموجود کردن"}
        </button>
        <button
          onClick={onClear}
          disabled={bulkLoading}
          type="button"
          className="flex-1 sm:flex-none px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          لغو انتخاب
        </button>
      </div>
    </div>
  );
}

const getProductStock = (item) =>
  item.option?.variations?.length
    ? item.option.variations.reduce((sum, v) => (v.checkStock ? sum + (Number(v.stock) || 0) : sum), 0)
    : Number(item.stock) || 0;

const isStockTracked = (item) =>
  item.option?.variations?.length
    ? item.option.variations.some((v) => v.checkStock)
    : Boolean(item.checkStock);

function StockCell({ stock, tracked }) {
  if (!tracked) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  return (
    <span className={stock <= 0 ? "text-red-600 dark:text-red-400 font-medium" : ""}>
      {digitsThree(stock)}
    </span>
  );
}

function ProductTable({ data, selectedProductIds, selectedVariations, onProductSelect, onVariationSelect, onEdit }) {
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSimpleIds = data.filter((p) => !p.option?.variations?.length).map((p) => String(p._id));
  const allSimpleSelected = allSimpleIds.length > 0 && allSimpleIds.every((id) => selectedProductIds.has(id));
  const someSelected =
    selectedProductIds.size > 0 ||
    [...selectedVariations.values()].some((s) => s.size > 0);

  const handleSelectAll = (checked) => {
    data.forEach((p) => {
      const pid = String(p._id);
      if (!p.option?.variations?.length) {
        onProductSelect(pid, checked);
      }
    });
  };

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-6">
      <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th className="px-4 py-3 w-10">
              <input
                type="checkbox"
                checked={allSimpleSelected}
                ref={(el) => { if (el) el.indeterminate = !allSimpleSelected && someSelected; }}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
            </th>
            <th className="px-3 py-3 w-8"></th>
            <th className="px-4 py-3">عکس</th>
            <th className="px-4 py-3">نام</th>
            <th className="px-4 py-3">قیمت پایه</th>
            <th className="px-4 py-3">موجودی</th>
            <th className="px-4 py-3">موجودی انبار</th>
            <th className="px-4 py-3">وضعیت</th>
            <th className="px-4 py-3">عملیات</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const pid = String(item._id);
            const hasVariations = item.option?.variations?.length > 0;
            const isExpanded = expandedIds.has(pid);
            const isChecked = selectedProductIds.has(pid);

            const varSet = selectedVariations.get(pid) || new Set();
            const varCount = item.option?.variations?.length || 0;
            const allVarSelected = varCount > 0 && varSet.size === varCount;
            const someVarSelected = varSet.size > 0 && varSet.size < varCount;

            return [
              <tr
                key={pid}
                className={`border-b dark:border-gray-700 transition-colors ${
                  isChecked || allVarSelected
                    ? "bg-blue-50 dark:bg-blue-900/20"
                    : someVarSelected
                    ? "bg-blue-50/40 dark:bg-blue-900/10"
                    : "odd:bg-white odd:dark:bg-gray-900 even:bg-gray-50 even:dark:bg-gray-800"
                }`}
              >
                <td className="px-4 py-3">
                  {hasVariations ? (
                    <input
                      type="checkbox"
                      checked={allVarSelected}
                      ref={(el) => { if (el) el.indeterminate = someVarSelected; }}
                      onChange={(e) => {
                        item.option.variations.forEach((v) => {
                          onVariationSelect(pid, String(v._id), e.target.checked);
                        });
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  ) : (
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => onProductSelect(pid, e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  )}
                </td>
                <td className="px-3 py-3">
                  {hasVariations && (
                    <button
                      onClick={() => toggleExpand(pid)}
                      type="button"
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                      title="نمایش آپشن‌ها"
                    >
                      <svg
                        className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  {item.mainImage ? (
                    <img className="w-10 h-10 rounded-md" src={`${fileUrl}files/thumb_${item.mainImage}`} />
                  ) : (
                    <DefaultImage imgSize={26} imgClass="!w-10 !h-10 rounded-md" />
                  )}
                </td>
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                  <div className="flex items-center gap-2">
                    <span>{item.name}</span>
                    {hasVariations && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                        ({varCount} آپشن)
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">{digitsThree(item.basePrice)}</td>
                <td className="px-4 py-3">
                  {item.inventoryStatus === "available" ? "موجود" : "نا موجود"}
                </td>
                <td className="px-4 py-3">
                  <StockCell stock={getProductStock(item)} tracked={isStockTracked(item)} />
                </td>
                <td className="px-4 py-3 text-xs">
                  {item.status === "active" ? "فعال" : item.status === "pending" ? "انتظار" : "غیرفعال"}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onEdit(item)}
                    className="font-medium cursor-pointer text-blue-600 dark:text-blue-500 hover:underline text-sm"
                  >
                    ویرایش
                  </button>
                </td>
              </tr>,

              ...(hasVariations && isExpanded
                ? item.option.variations.map((v) => {
                    const vid = String(v._id);
                    const varChecked = varSet.has(vid);
                    return (
                      <tr
                        key={`${pid}-${vid}`}
                        className={`border-b dark:border-gray-700 transition-colors ${
                          varChecked
                            ? "bg-blue-50 dark:bg-blue-900/20"
                            : "bg-gray-50/80 dark:bg-gray-800/60"
                        }`}
                      >
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-center pr-2">
                            <input
                              type="checkbox"
                              checked={varChecked}
                              onChange={(e) => onVariationSelect(pid, vid, e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                          </div>
                        </td>
                        <td className="px-3 py-2"></td>
                        <td className="px-4 py-2">
                          {v.image ? (
                            <img className="w-10 h-10 rounded-md" src={`${fileUrl}files/thumb_${v.image}`} />
                          ) : (
                            <DefaultImage imgSize={26} imgClass="!w-10 !h-10 rounded-md" />
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-400 text-xs">
                          <div className="flex items-center gap-1">
                            <span>{v.value}</span>
                            <span className="text-gray-400 text-xs">({v.sku})</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">{digitsThree(v.price)}</td>
                        <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                          {v.inventoryStatus === "available" ? "موجود" : "نا موجود"}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                          <StockCell stock={Number(v.stock) || 0} tracked={Boolean(v.checkStock)} />
                        </td>
                        <td className="px-4 py-2" />
                        <td className="px-4 py-2" />
                      </tr>
                    );
                  })
                : []),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

const FILTER_STORAGE_KEY = "productListFilter";

const loadSavedFilter = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY));
    return saved && typeof saved === "object" ? saved : {};
  } catch {
    return {};
  }
};

const saveFilter = (filter) => {
  try {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filter));
  } catch {}
};

export default function ProductList() {
  const [productData, setProductData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingJsonExport, setLoadingJsonExport] = useState(false);
  const [showPriceImport, setShowPriceImport] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [selectedVariations, setSelectedVariations] = useState(new Map());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [filter, setFilter] = useState(loadSavedFilter);

  const getProduct = async (params) => {
    const path = "admin/products";
    try {
      const response = await getApi({ path, params: { ...params } });
      const result = response?.data?.result;
      if (!result?.data?.length && params?.page > 1) {
        applyFilter({ ...params, page: 1 });
        return;
      }
      setProductData(result?.data);
      setPagination(result);
      setSelectedProductIds(new Set());
      setSelectedVariations(new Map());
    } catch (error) {
      console.log(error);
    }
  };

  const applyFilter = (nextFilter) => {
    setFilter(nextFilter);
    saveFilter(nextFilter);
    getProduct(nextFilter);
  };

  useEffect(() => {
    getProduct(filter);
  }, []);

  const filterDataHandler = (newParams) => {
    applyFilter({ ...filter, ...newParams });
  };

  const handleProductSelect = useCallback((pid, checked) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(pid) : next.delete(pid);
      return next;
    });
  }, []);

  const handleVariationSelect = useCallback((pid, vid, checked) => {
    setSelectedVariations((prev) => {
      const next = new Map(prev);
      const varSet = new Set(next.get(pid) || []);
      checked ? varSet.add(vid) : varSet.delete(vid);
      if (varSet.size === 0) next.delete(pid);
      else next.set(pid, varSet);
      return next;
    });
  }, []);

  const handleClear = useCallback(() => {
    setSelectedProductIds(new Set());
    setSelectedVariations(new Map());
  }, []);

  const handleBulkInventory = useCallback(
    async (inventoryStatus) => {
      const productIds = Array.from(selectedProductIds);
      const variationItems = [];
      selectedVariations.forEach((varSet, productId) => {
        varSet.forEach((variationId) => {
          variationItems.push({ productId, variationId });
        });
      });
      if (productIds.length === 0 && variationItems.length === 0) return;

      setBulkLoading(true);
      try {
        const response = await putApi({
          path: "admin/products/bulkUpdateInventoryStatus",
          body: JSON.stringify({ productIds, variationItems, inventoryStatus }),
        });
        const { updated } = response?.data?.result || {};
        successMessage(`${updated} آیتم با موفقیت به‌روزرسانی شد`);
        await getProduct(filter || {});
      } catch (err) {
        console.error(err);
        errorMessage("خطا در به‌روزرسانی وضعیت موجودی");
      } finally {
        setBulkLoading(false);
      }
    },
    [selectedProductIds, selectedVariations, filter]
  );

  const stripHtml = (html) => {
    if (!html) return "-";
    const spaced = html.replace(/<\/?(p|div|br|li|h[1-6]|blockquote|pre|tr|td|th)[^>]*\/?>/gi, " ");
    const doc = new DOMParser().parseFromString(spaced, "text/html");
    return doc.body.textContent?.replace(/\s+/g, " ").trim() || "-";
  };

  const handleExportExcel = async () => {
    const path = "admin/products/withSeparationVar";
    try {
      setLoadingExport(true);
      const response = await getApi({ path });
      const data = response?.data?.result || [];
      const exportData = data.map((item) => ({
        "نام محصول": item.name,
        "SKU": item.sku || "-",
        "دسته بندی": item.categories
          ? Array.isArray(item.categories)
            ? item.categories.map((cat) => (typeof cat === "string" ? cat : cat.name)).join(" - ")
            : item.categories
          : "-",
        "توضیحات کوتاه": stripHtml(item.shortDescription),
        "توضیحات": stripHtml(item.description),
        "تصویر اصلی": item.mainImage || "-",
        "قیمت پایه": item.basePrice,
        "قیمت خرید": item.buyPrice || "-",
        "وزن": item.weight ?? "-",
        "وضعیت موجودی": item.inventoryStatus === "available" ? "موجود" : "ناموجود",
        "وضعیت": item.status === "active" ? "فعال" : "غیرفعال",
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
      XLSX.writeFile(workbook, "products_export.xlsx");
    } catch (error) {
      console.log("Error exporting excel:", error);
    } finally {
      setLoadingExport(false);
    }
  };

  const handleExportJson = async () => {
    const path = "admin/products/withoutPaginate";
    try {
      setLoadingJsonExport(true);
      const response = await getApi({ path });
      const data = response?.data?.result || [];
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "products_export.json";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.log("Error exporting json:", error);
    } finally {
      setLoadingJsonExport(false);
    }
  };

  const navigate = useNavigate();
  const editProduct = (item) => {
    const found = productData.find((p) => String(p._id) === String(item._id));
    navigate("/edit-product", { state: found });
  };

  const hasSelection =
    selectedProductIds.size > 0 ||
    [...selectedVariations.values()].some((s) => s.size > 0);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <Title title={"لیست محصولات"} />
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            onClick={() => setShowPriceImport(true)}
            text="ورود قیمت از اکسل"
            color="success"
            type="button"
            className="whitespace-nowrap flex-1 sm:flex-none text-xs sm:text-sm"
          />
          <Button
            onClick={handleExportJson}
            text={loadingJsonExport ? "در حال دریافت..." : "خروجی JSON"}
            color="primary"
            type="button"
            className="whitespace-nowrap flex-1 sm:flex-none text-xs sm:text-sm"
          />
          <Button
            onClick={handleExportExcel}
            text={loadingExport ? "در حال دریافت..." : "خروجی اکسل"}
            color="success"
            type="button"
            className="whitespace-nowrap flex-1 sm:flex-none text-xs sm:text-sm"
          />
        </div>
      </div>
      <ProductFilter filter={filter} filterDataHandler={filterDataHandler} />

      {hasSelection && (
        <BulkActionBar
          selectedProductIds={selectedProductIds}
          selectedVariations={selectedVariations}
          bulkLoading={bulkLoading}
          onAvailable={() => handleBulkInventory("available")}
          onUnavailable={() => handleBulkInventory("unavailable")}
          onClear={handleClear}
        />
      )}

      <Pagination filterDataHandler={filterDataHandler} pagination={pagination} />
      <ProductTable
        data={productData || []}
        selectedProductIds={selectedProductIds}
        selectedVariations={selectedVariations}
        onProductSelect={handleProductSelect}
        onVariationSelect={handleVariationSelect}
        onEdit={editProduct}
      />
      <Pagination pagination={pagination} filterDataHandler={filterDataHandler} />

      {showPriceImport && (
        <PriceImportModal
          onClose={() => setShowPriceImport(false)}
          onSuccess={() => getProduct(filter || {})}
        />
      )}
    </div>
  );
}